/**
 * AttachClientModal Component
 *
 * Modal for attaching a client record to a project.
 * Shows list of all clients without an attached project.
 * Similar styling to AdminDashboard modals.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Close handler
 * @param {string} props.projectId - Project ID to attach client to
 * @param {function} props.onAttach - Callback after successful attachment
 */

import React from "react";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaSpinner,
  FaChevronRight,
} from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import "./AttachClientModal.css";

const AttachClientModal = ({ isOpen, onClose, projectId, onAttach }) => {
  const queryClient = useQueryClient();

  // Fetch unattached clients
  const {
    data: clients = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["unattachedClients"],
    queryFn: async () => {
      const getUnattachedClients = httpsCallable(
        functions,
        "getUnattachedClients",
      );
      const result = await getUnattachedClients();
      // Response format: {success: true, data: {clients: [...], count: ...}}
      return result.data.data.clients || [];
    },
    enabled: isOpen, // Only fetch when modal is open
  });

  // Attach client mutation
  const attachClientMutation = useMutation({
    mutationFn: async (clientId) => {
      const attachClientToProject = httpsCallable(
        functions,
        "attachClientToProject",
      );
      const result = await attachClientToProject({
        projectId,
        clientId,
      });
      return result.data;
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["unattachedClients"] });

      // Call parent callback
      if (onAttach) {
        onAttach();
      }

      // Close modal
      onClose();
    },
    onError: (error) => {
      console.error("Error attaching client:", error);
      alert(`Failed to attach client: ${error.message}`);
    },
  });

  const handleClientClick = (clientId) => {
    if (window.confirm("Attach this client to the project?")) {
      attachClientMutation.mutate(clientId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="acm-modal-overlay" onClick={onClose}>
      <div className="acm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acm-modal-header">
          <div className="acm-modal-title-row">
            <div className="acm-modal-header-icon">
              <FaUser />
            </div>
            <h3>Attach Client Record</h3>
          </div>
          <button
            className="acm-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="acm-modal-content">
          {isLoading && (
            <div className="acm-loading">
              <FaSpinner className="acm-spinner" />
              <span>Loading clients...</span>
            </div>
          )}

          {isError && (
            <div className="acm-error">
              <p>Error loading clients</p>
              <p className="acm-error-message">
                {error?.message || "Unknown error"}
              </p>
            </div>
          )}

          {!isLoading && !isError && clients.length === 0 && (
            <p className="acm-modal-empty">
              No unattached clients found. All clients have projects assigned.
            </p>
          )}

          {!isLoading && !isError && clients.length > 0 && (
            <>
              <p className="acm-modal-subtitle">
                Select a client to attach to this project:
              </p>
              {clients.map((client) => (
                <button
                  key={client.id}
                  className="acm-modal-item acm-modal-item-clickable"
                  onClick={() => handleClientClick(client.id)}
                  disabled={attachClientMutation.isPending}
                >
                  <div className="acm-item-info">
                    <span className="acm-item-title">{client.name}</span>
                    {client.email && (
                      <span className="acm-item-detail">
                        <FaEnvelope /> {client.email}
                      </span>
                    )}
                    <FaChevronRight className="acm-item-arrow" />
                  </div>
                </button>
              ))}
            </>
          )}

          {attachClientMutation.isPending && (
            <div className="acm-loading-overlay">
              <FaSpinner className="acm-spinner" />
              <span>Attaching client...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachClientModal;
